import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { Send, Download, ChevronDown, ChevronRight, FileDown, CreditCard, Loader2, ReceiptText } from "lucide-react";
import { api, useAuth, formatApiError } from "@/lib/auth";
import { openFile, openQuotePdf, openInvoicePdf } from "@/lib/downloads";
import { STATUS_LABELS, STATUS_LIST, eur } from "@/lib/constants";
import { fmtDateTime, fmtDate } from "@/lib/datetime";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";
import CreativePhaseTimeline, { CREATIVE_PHASES, CREATIVE_PHASE_LABELS } from "@/components/project/CreativePhaseTimeline";
import ProposalsSection from "@/components/project/ProposalsSection";
import FilesLibrary from "@/components/project/FilesLibrary";
import RevisionLinkCard from "@/components/project/RevisionLinkCard";
import { OrderBillingSetup } from "@/components/intermediary/OrderBillingSetup";
import { AdminEndClientPayments } from "@/components/intermediary/AdminEndClientPayments";
import { BalanceRemindersCard } from "@/components/intermediary/BalanceRemindersCard";

function eurFr(n) {
  const v = Number(n || 0);
  if (Math.abs(v - Math.round(v)) < 0.005) return `${Math.round(v)} €`;
  return `${v.toFixed(2).replace(".", ",")} €`;
}

function RequestInvoiceButton({ order }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const auto = params.get("requestInvoice") === "1";

  const request = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/request-invoice`);
      const invId = data?.invoice?.id;
      const number = data?.invoice?.number;
      if (data?.created) toast.success(`Facture ${number} générée`);
      else toast.success(`Facture ${number} déjà disponible`);
      if (invId) {
        await openInvoicePdf(invId);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.reason === "billing_incomplete") {
        toast.message(detail.message);
        nav(detail.return_to || `/espace/facturation?return=/espace/commande/${order.id}?requestInvoice=1`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  }, [order.id, nav]);

  // Auto-trigger once when arriving from the billing page (?requestInvoice=1)
  useEffect(() => {
    if (auto && order.fully_paid) {
      // Clear the query param first so a refresh doesn't loop
      window.history.replaceState({}, "", `/espace/commande/${order.id}`);
      request();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto]);

  if (!order.fully_paid) return null;
  return (
    <button onClick={request} disabled={busy}
      className="cs-btn-primary w-full justify-center inline-flex items-center gap-2 mt-3"
      data-testid="request-invoice">
      {busy ? <Loader2 size={14} className="animate-spin" /> : <ReceiptText size={14} />}
      Demander ma facture
    </button>
  );
}

function QuoteBlock({ order, isAdmin, onReload }) {
  const [quotePrice, setQuotePrice] = useState(String(order.quote?.price || order.price || 0));
  const [validity, setValidity] = useState(String(order.quote?.validity_days || 30));
  const [note, setNote] = useState(order.quote?.note || "");
  const [busy, setBusy] = useState(false);

  const canCreateQuote = isAdmin && order.requires_validation && ["a_valider","devis_en_preparation","devis_refuse","devis_expire"].includes(order.status);
  const canSendQuote = isAdmin && order.quote && !order.quote.sent_at;
  const canClientDecide = !isAdmin && order.status === "devis_envoye" && order.quote?.sent_at;

  const createOrUpdateQuote = async () => {
    try {
      setBusy(true);
      await api.post(`/admin/orders/${order.id}/quote/create`, {
        price: Number(quotePrice), validity_days: Number(validity), note,
      });
      toast.success("Devis préparé");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const sendQuote = async () => {
    try {
      setBusy(true);
      await api.post(`/admin/orders/${order.id}/quote/send`);
      toast.success("Devis envoyé au client");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const decide = async (decision) => {
    try {
      setBusy(true);
      await api.post(`/orders/${order.id}/quote/${decision}`);
      toast.success(decision === "accept" ? "Devis accepté" : "Devis refusé");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!order.requires_validation && !order.quote) return null;

  return (
    <div className="cs-card p-6" data-testid="quote-block">
      <div className="cs-eyebrow text-[#0E7C90]">Devis</div>

      {order.quote && (
        <div className="mt-3 space-y-2 text-sm" data-testid="quote-details">
          <div className="flex justify-between border-b border-black/10 py-1.5">
            <span className="text-neutral-500">Numéro</span>
            <span className="font-mono">{order.quote.number} · v{order.quote.version}</span>
          </div>
          <div className="flex justify-between border-b border-black/10 py-1.5">
            <span className="text-neutral-500">Montant du devis</span>
            <span className="font-mono">{eurFr(order.quote.price)}</span>
          </div>
          <div className="flex justify-between border-b border-black/10 py-1.5">
            <span className="text-neutral-500">Validité</span>
            <span className="font-mono">{order.quote.validity_days} jours{order.quote.expires_at ? ` · jusqu'au ${fmtDate(order.quote.expires_at)}` : ""}</span>
          </div>
          {order.quote.note && (
            <div className="border-b border-black/10 py-1.5">
              <div className="text-neutral-500 text-[11px] font-mono uppercase tracking-[0.14em]">Note</div>
              <div className="mt-1 whitespace-pre-wrap">{order.quote.note}</div>
            </div>
          )}
        </div>
      )}

      {!isAdmin && order.requires_validation && !order.quote && (
        <p className="mt-3 text-sm text-neutral-700">
          Votre projet nécessite une vérification par Charbonier Studio avant sa mise en production. Aucun paiement ne vous sera demandé avant validation.
        </p>
      )}

      {canClientDecide && (
        <div className="mt-4 flex gap-2 flex-wrap">
          <button onClick={() => decide("accept")} disabled={busy}
            className="cs-btn-primary" data-testid="quote-accept">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Accepter le devis
          </button>
          <button onClick={() => decide("refuse")} disabled={busy}
            className="cs-btn-ghost !text-black !border-black" data-testid="quote-refuse">
            Refuser
          </button>
          <button type="button" onClick={() => openQuotePdf(order.id)}
             className="cs-btn-ghost !text-black !border-black inline-flex items-center gap-2">
            <FileDown size={14} /> Télécharger le devis PDF
          </button>
        </div>
      )}

      {isAdmin && (canCreateQuote || canSendQuote) && (
        <div className="mt-5 border-t border-black/10 pt-4 space-y-3" data-testid="admin-quote-form">
          {canCreateQuote && (
            <>
              <label className="cs-eyebrow text-neutral-500">Montant du devis (€)</label>
              <input type="number" min="0" step="0.01" value={quotePrice} onChange={(e) => setQuotePrice(e.target.value)}
                     className="cs-input" data-testid="admin-quote-price" />
              <label className="cs-eyebrow text-neutral-500 mt-3 block">Validité (jours)</label>
              <input type="number" min="1" max="180" value={validity} onChange={(e) => setValidity(e.target.value)}
                     className="cs-input" data-testid="admin-quote-validity" />
              <label className="cs-eyebrow text-neutral-500 mt-3 block">Note</label>
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                        className="cs-input" placeholder="Observations…" data-testid="admin-quote-note" />
              <button onClick={createOrUpdateQuote} disabled={busy}
                className="cs-btn-ghost !text-black !border-black w-full justify-center" data-testid="admin-quote-save">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null} {order.quote ? "Mettre à jour le devis" : "Créer le devis"}
              </button>
            </>
          )}
          {canSendQuote && (
            <button onClick={sendQuote} disabled={busy}
              className="cs-btn-primary w-full justify-center" data-testid="admin-quote-send">
              {busy ? <Loader2 size={14} className="animate-spin" /> : null} Envoyer le devis au client
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PaymentBlock({ order, isAdmin, onReload }) {
  const [busy, setBusy] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const plan = order.payment_plan;
  const payStatuses = ["attente_paiement","attente_acompte","paiement_intermediaire_a_regler","solde_a_regler"];
  const quoteStatuses = ["a_valider","devis_en_preparation","devis_envoye","devis_refuse","devis_expire"];
  const isQuotePhase = quoteStatuses.includes(order.status);
  const { user } = useAuth();
  const awaitingEndClient = order.payer === "end_client";
  const needsBillingConfirm = ["clients", "both"].includes(user?.account_usage) && !order.billing_confirmed;
  const shouldPay = !isAdmin && plan && !isQuotePhase && payStatuses.includes(order.status) && !order.fully_paid && !awaitingEndClient;

  const pay = async (step_key) => {
    try {
      setBusy(true);
      const { data } = await api.post(`/orders/${order.id}/checkout`, {
        step_key, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  const adminAction = async (path, ok) => {
    try {
      setBusy(true);
      await api.post(`/admin/orders/${order.id}/${path}`);
      toast.success(ok);
      onReload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  // Bug #2 — pendant la phase devis, aucune UI de paiement ne doit apparaître.
  if (isQuotePhase) {
    if (isAdmin) return null;
    return (
      <div className="cs-card p-6" data-testid="payment-block-pending">
        <div className="cs-eyebrow text-[#0E7C90]">Paiement</div>
        <p className="mt-3 text-sm text-neutral-700">
          Un devis est en cours de préparation. Vous pourrez régler votre commande dès que le devis vous sera envoyé et accepté.
        </p>
      </div>
    );
  }
  if (!plan) return null;

  const nextPending = (plan.steps || []).find((s) => s.status !== "paid");
  const hasMilestoneStep = (plan.steps || []).some((s) => s.key === "milestone");
  const milestonePaid = (plan.steps || []).find((s) => s.key === "milestone")?.status === "paid";
  const balancePaid = (plan.steps || []).find((s) => s.key === "balance")?.status === "paid";
  const canRequestMilestone = isAdmin && hasMilestoneStep && !milestonePaid && ["acompte_recu","en_cours","bat_valide","bat_envoye"].includes(order.status);
  const canRequestBalance = isAdmin && !balancePaid && (plan.plan_type !== "full") &&
    (plan.plan_type === "50_50" ? ["acompte_recu","en_cours","bat_valide"].includes(order.status)
      : ["paiement_intermediaire_recu","en_cours","bat_valide"].includes(order.status));

  return (
    <div className="cs-card p-6" data-testid="payment-block">
      <div className="cs-eyebrow text-[#0E7C90]">Paiement</div>
      <div className="mt-3 text-sm">
        <div className="flex justify-between py-1.5 border-b border-black/10">
          <span className="text-neutral-500">Total</span>
          <span className="font-mono">{eurFr(order.price)}</span>
        </div>
        <div className="flex justify-between py-1.5 border-b border-black/10">
          <span className="text-neutral-500">Déjà payé</span>
          <span className="font-mono text-[#0E7C90]">{eurFr(order.amount_paid)}</span>
        </div>
        <div className="flex justify-between py-1.5 border-b border-black/10">
          <span className="text-neutral-500">Reste à régler</span>
          <span className="font-mono">{eurFr(order.amount_due)}</span>
        </div>
      </div>

      <ul className="mt-4 space-y-2" data-testid="payment-steps">
        {(plan.steps || []).map((s) => (
          <li key={s.key} className={`flex items-center justify-between py-2 px-3 border ${s.status === "paid" ? "border-[#0E7C90]/40 bg-[#0E7C90]/5" : "border-black/15 bg-white"}`}>
            <div className="text-sm">
              <div className="font-medium">{s.label}</div>
              <div className="text-[12px] font-mono text-neutral-500">
                {s.status === "paid" ? `Payé le ${s.paid_at ? fmtDate(s.paid_at) : "—"}` : "En attente"}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono">{eurFr(s.amount)}</div>
              {!isAdmin && awaitingEndClient && s.status !== "paid" && s.key === nextPending?.key && (
                <div className="text-[12px] font-mono text-[#0E7C90] mt-1" data-testid={`awaiting-end-client-${s.key}`}>Règlement attendu de votre client</div>
              )}
              {shouldPay && s.status !== "paid" && s.key === nextPending?.key && (
                <button onClick={() => pay(s.key)} disabled={busy || !cgvAccepted || needsBillingConfirm}
                  className="cs-btn-primary !py-1.5 !px-3 text-[13px] mt-1 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid={`pay-step-${s.key}`}>
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
                  Régler {eurFr(s.amount)}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {shouldPay && needsBillingConfirm && (
        <p className="mt-3 text-[12px] font-mono text-[#0E7C90]" data-testid="pay-needs-billing-confirm">Confirmez d'abord le client final et le destinataire de la facture.</p>
      )}
      {!isAdmin && awaitingEndClient && !order.fully_paid && (
        <p className="mt-3 text-[13px] text-neutral-600" data-testid="awaiting-end-client-note">
          Charbonier Studio adresse directement la demande de règlement à votre client. Vous pouvez aussi choisir de régler vous-même dans « Client final &amp; facturation ».
        </p>
      )}
      {shouldPay && (
        <label className="mt-4 flex items-start gap-2 text-[13px] text-neutral-700" data-testid="cgv-consent-label">
          <input
            type="checkbox"
            checked={cgvAccepted}
            onChange={(e) => setCgvAccepted(e.target.checked)}
            className="mt-1"
            data-testid="cgv-consent"
            required
          />
          <span>
            J'ai lu et j'accepte les <Link to="/cgv" className="cs-link-underline text-[#0E7C90]">conditions générales de vente</Link>
            {" "}et la <Link to="/confidentialite" className="cs-link-underline text-[#0E7C90]">politique de confidentialité</Link>.
          </span>
        </label>
      )}

      {isAdmin && (canRequestMilestone || canRequestBalance || order.fully_paid) && (
        <div className="mt-4 border-t border-black/10 pt-3 space-y-2" data-testid="admin-payment-actions">
          {canRequestMilestone && (
            <button onClick={() => adminAction("request-milestone", "Deuxième règlement demandé")}
              disabled={busy}
              className="cs-btn-ghost !text-black !border-black w-full justify-center"
              data-testid="admin-request-milestone">
              Demander le deuxième règlement
            </button>
          )}
          {canRequestBalance && (
            <button onClick={() => adminAction("request-balance", "Solde demandé")}
              disabled={busy}
              className="cs-btn-ghost !text-black !border-black w-full justify-center"
              data-testid="admin-request-balance">
              Demander le solde
            </button>
          )}
          {order.fully_paid && order.status !== "termine" && (
            <button onClick={() => adminAction("mark-completed", "Prestation marquée terminée")}
              disabled={busy}
              className="cs-btn-primary w-full justify-center"
              data-testid="admin-mark-completed">
              Marquer la prestation terminée
            </button>
          )}
        </div>
      )}

      {order.final_files_locked && (
        <div className="mt-4 text-[12px] font-mono text-neutral-500">
          Les fichiers définitifs seront accessibles dès réception du solde.
        </div>
      )}

      {!isAdmin && <RequestInvoiceButton order={order} />}
    </div>
  );
}

function CouvertureAdaptationBriefBlock({ order }) {
  const b = order?.brief || {};
  const cur = b.current || {};
  const nf = b.new_format || {};
  const mo = b.modifications || {};
  const fi = b.files || {};
  const ex = order.extra || {};

  const coverLabel = { front_only: "Première de couverture seule", full: "Couverture complète (1re + dos + 4e)" }[cur.cover_type] || "—";
  const reasonMap = {
    printer: "Changement d'imprimeur / plateforme", format: "Changement de format",
    spine: "Modification de l'épaisseur du dos", specs: "Nouvelles spécifications techniques",
    multiple: "Plusieurs raisons", other: "Autre",
  };
  const reasonsList = (cur.reasons || []).map(r => reasonMap[r] || r).join(", ")
    + (cur.reasons?.includes("other") && cur.reason_other ? ` — ${cur.reason_other}` : "");
  const tplLabel = { yes: "Fourni par l'imprimeur", no: "Non fourni", unknown: "À vérifier" }[nf.template_provided] || "—";
  const bindLabel = nf.binding === "other" ? `Autre — ${nf.binding_other || "à préciser"}`
    : { softcover: "Broché / couverture souple", hardcover: "Relié / couverture rigide", unknown: "Non renseignée" }[nf.binding] || "—";
  const modeLabel = { adaptation_only: "Adaptation technique seule (création conservée)", small_changes: "Adaptation + petites modifications" }[mo.mode] || "—";
  const isFull = cur.cover_type === "full";

  const Row = ({ k, v }) => v ? (
    <div className="grid grid-cols-[160px_1fr] gap-3 py-1 text-sm">
      <span className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em] pt-0.5">{k}</span>
      <span className="text-neutral-800 whitespace-pre-wrap">{v}</span>
    </div>
  ) : null;
  const Section = ({ title, children }) => (
    <div className="border-t border-black/10 pt-3 mt-3 first:border-t-0 first:pt-0 first:mt-0">
      <div className="cs-eyebrow text-[#0E7C90] mb-2">{title}</div>
      <div>{children}</div>
    </div>
  );

  const fileGroups = [
    ["Couverture actuelle", ex.current_cover_file_ids],
    ["Nouveau gabarit", ex.new_template_file_ids],
    ["Nouveau logo", ex.new_logo_file_ids],
    ["Nouveau code-barres", ex.new_barcode_file_ids],
    ["Joboptions / consignes imprimeur", ex.joboptions_file_ids],
    ["Autres éléments", ex.other_file_ids],
  ].filter(([, ids]) => ids && ids.length > 0);

  return (
    <div className="cs-card p-6" data-testid="couverture-adaptation-brief-block">
      <div className="cs-eyebrow text-[#0E7C90]">Brief éditorial — Adaptation d'une couverture existante</div>

      <Section title="Couverture actuelle">
        <Row k="Type" v={coverLabel} />
        <Row k="Raison(s)" v={reasonsList || "—"} />
        <Row k="Précisions" v={cur.details} />
      </Section>

      <Section title="Nouveau format & imprimeur">
        <Row k="Imprimeur" v={nf.printer_platform} />
        <Row k="Gabarit imprimeur" v={tplLabel} />
        <Row k="Format fini" v={nf.width_mm && nf.height_mm ? `${nf.width_mm} × ${nf.height_mm} mm` : "—"} />
        {isFull && <>
          <Row k="Nombre de pages" v={nf.page_count ? String(nf.page_count) : null} />
          <Row k="Épaisseur du dos" v={nf.spine_known === "yes" && nf.spine_mm ? `${nf.spine_mm} mm` : "À vérifier avec l'imprimeur"} />
          <Row k="Reliure" v={bindLabel} />
        </>}
        <Row k="Consignes / Joboptions" v={nf.printer_specs_notes} />
      </Section>

      <Section title="Modifications">
        <Row k="Nature" v={modeLabel} />
        {mo.mode === "small_changes" && <Row k="Détails" v={mo.notes} />}
      </Section>

      <Section title="Fichiers">
        {fi.no_source_file && (
          <div className="text-sm text-neutral-700 mb-2">• Le client ne possède pas le fichier source éditable.</div>
        )}
        {fileGroups.length > 0 ? (
          <div className="text-sm space-y-1">
            {fileGroups.map(([label, ids]) => (
              <div key={label}>• {label} : {ids.length} fichier(s)</div>
            ))}
            <div className="text-[12px] text-neutral-500 mt-2">Détail avec liens de téléchargement dans la section « Fichiers reçus » ci-dessous.</div>
          </div>
        ) : (
          <div className="text-sm text-neutral-500">Aucun fichier transmis.</div>
        )}
      </Section>
    </div>
  );
}

function CouvertureCompleteBriefBlock({ order }) {
  const b = order?.brief || {};
  const bk = b.book || {};
  const st = b.style || {};
  const bc = b.back || {};
  const isbn = b.isbn || {};
  const fm = b.format || {};
  const ex = order.extra || {};

  const editionLabel = {
    publisher: "Publié par une maison d'édition",
    self: "Autoédition",
    undecided: "Pas encore défini",
  }[bk.edition_status] || "—";
  const logoDisplay = {
    yes: "Oui", no: "Non", unsure: "Je ne sais pas encore",
  }[bk.publisher_logo_display] || null;
  const logoLocs = {
    front: "Première de couverture",
    spine: "Dos / tranche",
    back: "Quatrième de couverture",
    studio: "Laissé au studio",
  };
  const idea = { precise: "Idée précise", some: "Quelques pistes", none: "Direction laissée au studio" }[st.idea_precision] || "—";
  const imgSrc = { client: "Fournie par le client", studio: "Recherche par le studio", unsure: "À décider" }[st.image_source] || "—";
  const hasIsbn = { yes: "Oui", not_yet: "Pas encore", unknown: "Non concerné / à décider" }[isbn.has_isbn] || "—";
  const barSrc = { client: "Fourni par le client", studio: "Généré depuis l'ISBN (inclus)", later: "À fournir ultérieurement" }[isbn.barcode_source] || "—";
  const tplLabel = { yes: "Oui — fourni", no: "Non", unknown: "À vérifier" }[fm.template_provided] || "—";
  const bindLabel = fm.binding === "other" ? (fm.binding_other ? `Autre — ${fm.binding_other}` : "Autre")
    : { softcover: "Broché / couverture souple", hardcover: "Relié / couverture rigide", unknown: "Non renseignée" }[fm.binding] || "—";

  const Row = ({ k, v }) => v ? (
    <div className="grid grid-cols-[160px_1fr] gap-3 py-1 text-sm">
      <span className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em] pt-0.5">{k}</span>
      <span className="text-neutral-800 whitespace-pre-wrap">{v}</span>
    </div>
  ) : null;
  const Section = ({ title, children }) => (
    <div className="border-t border-black/10 pt-3 mt-3 first:border-t-0 first:pt-0 first:mt-0">
      <div className="cs-eyebrow text-[#0E7C90] mb-2">{title}</div>
      <div>{children}</div>
    </div>
  );

  const spineList = (bc.spine_elements || []).map(x => x === "Autre" && bc.spine_element_other ? `Autre — ${bc.spine_element_other}` : x).join(", ");
  const logoLocList = (bk.publisher_logo_locations || []).map(k => logoLocs[k] || k).join(", ");
  const filesCount = (ex.publisher_logo_file_ids?.length || 0)
    + (ex.author_photo_file_ids?.length || 0)
    + (ex.barcode_file_ids?.length || 0)
    + (ex.template_file_ids?.length || 0)
    + (ex.printer_specs_file_ids?.length || 0)
    + (ex.element_file_ids?.length || 0);

  return (
    <div className="cs-card p-6" data-testid="couverture-complete-brief-block">
      <div className="cs-eyebrow text-[#0E7C90]">Brief éditorial — Couverture complète (1re, dos, 4e)</div>

      <Section title="Votre livre">
        <Row k="Titre" v={bk.title} />
        <Row k="Sous-titre" v={bk.subtitle} />
        <Row k="Auteur" v={bk.author} />
        <Row k="Collection / série" v={bk.collection} />
        <Row k="Tome" v={bk.tome} />
        <Row k="Genre" v={bk.genre === "Autre" ? bk.genre_other : bk.genre} />
        <Row k="Public" v={bk.audience} />
        <Row k="Situation" v={editionLabel} />
        {bk.edition_status === "publisher" && (<>
          <Row k="Éditeur" v={bk.publisher_name} />
          <Row k="Logo éditeur" v={logoDisplay} />
          {bk.publisher_logo_display === "yes" && <Row k="Emplacement(s) logo" v={logoLocList || "—"} />}
        </>)}
        <Row k="Résumé du livre" v={bk.synopsis} />
      </Section>

      <Section title="Univers graphique">
        <Row k="Ambiance(s)" v={(st.moods || []).join(", ") + (st.mood_other ? ` (${st.mood_other})` : "")} />
        <Row k="Niveau d'idée" v={idea} />
        <Row k="Éléments souhaités" v={st.elements_wanted} />
        <Row k="À éviter" v={st.elements_avoided} />
        <Row k="Couleurs" v={st.colors} />
        <Row k="Image principale" v={imgSrc} />
      </Section>

      <Section title="Dos / tranche & Quatrième de couverture">
        <Row k="Éléments du dos" v={spineList} />
        <Row k="Texte 4e de couv." v={bc.back_text_later ? "À transmettre ultérieurement" : bc.back_text} />
        <Row k="Biographie auteur" v={bc.author_bio} />
        <Row k="Citation / recommandation" v={bc.quote} />
        <Row k="Autres informations" v={bc.other_info} />
      </Section>

      <Section title="ISBN & code-barres">
        <Row k="ISBN" v={isbn.has_isbn === "yes" ? (isbn.isbn13 || "Fourni (numéro non saisi)") : hasIsbn} />
        {isbn.has_isbn === "yes" && <Row k="Code-barres" v={barSrc} />}
        <Row k="Prix public affiché" v={isbn.public_price} />
        <Row k="Consignes code-barres" v={isbn.barcode_notes} />
      </Section>

      <Section title="Format, fabrication & imprimeur">
        <Row k="Gabarit imprimeur" v={tplLabel} />
        <Row k="Format fini" v={fm.width_mm && fm.height_mm ? `${fm.width_mm} × ${fm.height_mm} mm` : "—"} />
        <Row k="Nombre de pages" v={fm.page_count ? String(fm.page_count) : null} />
        <Row k="Épaisseur du dos" v={fm.spine_known === "yes" && fm.spine_mm ? `${fm.spine_mm} mm` : "À vérifier avec l'imprimeur"} />
        <Row k="Reliure" v={bindLabel} />
        <Row k="Imprimeur / plateforme" v={fm.printer_platform} />
        <Row k="Consignes / Joboptions" v={fm.printer_specs_notes} />
      </Section>

      {filesCount > 0 && (
        <Section title="Fichiers transmis (comptés)">
          <div className="text-sm space-y-1">
            {ex.publisher_logo_file_ids?.length > 0 && <div>• Logo éditeur : {ex.publisher_logo_file_ids.length} fichier(s)</div>}
            {ex.author_photo_file_ids?.length > 0 && <div>• Photo auteur : {ex.author_photo_file_ids.length} fichier(s)</div>}
            {ex.barcode_file_ids?.length > 0 && <div>• Code-barres : {ex.barcode_file_ids.length} fichier(s)</div>}
            {ex.template_file_ids?.length > 0 && <div>• Gabarit imprimeur : {ex.template_file_ids.length} fichier(s)</div>}
            {ex.printer_specs_file_ids?.length > 0 && <div>• Consignes / Joboptions imprimeur : {ex.printer_specs_file_ids.length} fichier(s)</div>}
            {ex.element_file_ids?.length > 0 && <div>• Éléments (images, croquis, références) : {ex.element_file_ids.length} fichier(s)</div>}
            <div className="text-[12px] text-neutral-500 mt-2">Détail avec liens de téléchargement dans la section « Fichiers reçus » ci-dessous.</div>
          </div>
        </Section>
      )}
    </div>
  );
}

function CouvertureBriefBlock({ order }) {
  const b = order?.brief;
  if (!b) return null;
  const isFull = order?.type === "couverture_complete_config";
  const isFront = order?.type === "couverture_config";
  const isAdapt = order?.type === "couverture_adaptation_config";
  if (!isFull && !isFront && !isAdapt) return null;
  if (isFull) return <CouvertureCompleteBriefBlock order={order} />;
  if (isAdapt) return <CouvertureAdaptationBriefBlock order={order} />;
  const bk = b.book || {};
  const st = b.style || {};
  const fm = b.format || {};
  const ex = order.extra || {};
  const editionLabel = {
    publisher: "Publié par une maison d'édition",
    self: "Autoédition",
    undecided: "Pas encore défini",
  }[bk.edition_status] || bk.edition_status || "—";
  const logoDisplay = {
    yes: "Oui", no: "Non", unsure: "Je ne sais pas encore",
  }[bk.publisher_logo_display] || null;
  const logoPos = {
    "bottom-left": "En bas à gauche", "bottom-center": "En bas au centre",
    "bottom-right": "En bas à droite", "other": "Autre",
    "studio": "Laissé au studio",
  }[bk.publisher_logo_position] || null;
  const idea = { precise: "Idée précise", some: "Quelques pistes", none: "Aucune direction, à décider avec le studio" }[st.idea_precision] || "—";
  const imgSrc = { client: "Fournie par le client", studio: "Recherche par le studio", unsure: "À décider" }[st.image_source] || "—";
  const destLabel = (d) => ({ print: "Impression", digital: "Numérique", both: "Impression + numérique" })[d] || d;
  const delivLabel = (d) => ({ psd_print: "PSD impression (aplati, CMJN, 300 ppp)", digital: "Version numérique (RVB)", pdf_print: "PDF prêt imprimeur" })[d] || d;
  const specsStatus = { yes: "Consignes fournies", no: "Pas de consignes", unsure: "Ne sait pas" }[fm.printer_specs_status] || "—";

  const Row = ({ k, v }) => v ? (
    <div className="grid grid-cols-[160px_1fr] gap-3 py-1 text-sm">
      <span className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em] pt-0.5">{k}</span>
      <span className="text-neutral-800 whitespace-pre-wrap">{v}</span>
    </div>
  ) : null;
  const Section = ({ title, children }) => (
    <div className="border-t border-black/10 pt-3 mt-3 first:border-t-0 first:pt-0 first:mt-0">
      <div className="cs-eyebrow text-[#0E7C90] mb-2">{title}</div>
      <div>{children}</div>
    </div>
  );

  return (
    <div className="cs-card p-6" data-testid="couverture-brief-block">
      <div className="cs-eyebrow text-[#0E7C90]">Brief éditorial — Première de couverture</div>

      <Section title="Livre">
        <Row k="Titre" v={bk.title} />
        <Row k="Sous-titre" v={bk.subtitle} />
        <Row k="Auteur" v={bk.author} />
        <Row k="Genre" v={bk.genre === "Autre" ? bk.genre_other : bk.genre} />
        <Row k="Public" v={bk.audience} />
        <Row k="Situation" v={editionLabel} />
        {bk.edition_status === "publisher" && (<>
          <Row k="Éditeur" v={bk.publisher_name} />
          <Row k="Logo éditeur" v={logoDisplay} />
          {bk.publisher_logo_display === "yes" && <Row k="Position logo" v={logoPos === "Autre" ? `Autre — ${bk.publisher_logo_position_other}` : logoPos} />}
        </>)}
        <Row k="Résumé" v={bk.synopsis} />
      </Section>

      <Section title="Direction graphique">
        <Row k="Ambiances" v={(st.moods || []).join(", ") + (st.mood_other ? ` (${st.mood_other})` : "")} />
        <Row k="Niveau d'idée" v={idea} />
        <Row k="Éléments souhaités" v={st.elements_wanted} />
        <Row k="À éviter" v={st.elements_avoided} />
        <Row k="Couleurs" v={st.colors} />
        <Row k="Image principale" v={imgSrc} />
      </Section>

      <Section title="Format & livraison">
        <Row k="Dimensions" v={fm.dimensions_unknown ? "À définir" : (fm.width_mm && fm.height_mm ? `${fm.width_mm} × ${fm.height_mm} mm` : "—")} />
        <Row k="Destination" v={(fm.destinations || []).map(destLabel).join(", ")} />
        <Row k="Imprimeur / plateforme" v={fm.printer_platform} />
        <Row k="Spécifications imprimeur" v={specsStatus} />
        {fm.printer_specs_status === "yes" && <Row k="Consignes imprimeur" v={fm.printer_specs_notes} />}
        <Row k="Livrables demandés" v={(fm.deliverables || []).map(delivLabel).join(" · ")} />
      </Section>

      {((ex.publisher_logo_file_ids?.length || 0) + (ex.element_file_ids?.length || 0) + (ex.printer_specs_file_ids?.length || 0)) > 0 && (
        <Section title="Fichiers transmis (comptés)">
          <div className="text-sm space-y-1">
            {ex.publisher_logo_file_ids?.length > 0 && <div>• Logo éditeur : {ex.publisher_logo_file_ids.length} fichier(s)</div>}
            {ex.element_file_ids?.length > 0 && <div>• Éléments (images, croquis, références) : {ex.element_file_ids.length} fichier(s)</div>}
            {ex.printer_specs_file_ids?.length > 0 && <div>• Consignes / Joboptions imprimeur : {ex.printer_specs_file_ids.length} fichier(s)</div>}
            <div className="text-[12px] text-neutral-500 mt-2">Détail avec liens de téléchargement dans la section « Fichiers du client » ci-dessous.</div>
          </div>
        </Section>
      )}
    </div>
  );
}

function OrderSummaryCard({ order, isAdmin }) {
  const [showRaw, setShowRaw] = useState(false);
  const summary = order.summary || { narrative: "", sections: [] };
  const hasRaw = (Object.keys(order.answers || {}).length + Object.keys(order.technical || {}).length) > 0;
  const clientLabel = isAdmin ? "Résumé de la demande" : "Votre demande";
  const detailLabel = isAdmin ? "Détails du projet" : "Vos choix";
  return (
    <div className="cs-card p-6" data-testid="order-summary-card">
      <div className="cs-eyebrow text-[#0E7C90]">{clientLabel}</div>
      {summary.narrative ? (
        <p className="mt-3 text-[15px] leading-relaxed" data-testid="order-summary-narrative">
          {summary.narrative}
        </p>
      ) : (
        <p className="mt-3 text-[15px] leading-relaxed text-neutral-500">
          Résumé non disponible — voir la description ci-dessous.
        </p>
      )}
      {order.description && !summary.narrative && (
        <p className="mt-3 whitespace-pre-wrap text-sm">{order.description}</p>
      )}
      {order.description && summary.narrative && order.description.trim() !== summary.narrative.trim() && (
        <div className="mt-4">
          <div className="cs-eyebrow text-neutral-500">Message du client</div>
          <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700">{order.description}</p>
        </div>
      )}
      {summary.sections?.length > 0 && (
        <>
          <div className="cs-eyebrow text-neutral-500 mt-6">{detailLabel}</div>
          <dl className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {summary.sections.map((s, i) => (
              <div key={i} className={s.values ? "sm:col-span-2" : ""}>
                <dt className="text-neutral-500 font-mono text-[11px] uppercase tracking-[0.14em]">{s.label}</dt>
                {s.values ? (
                  <dd className="mt-1"><ul className="list-disc pl-5 space-y-1">{s.values.map((v, j) => <li key={j}>{v}</li>)}</ul></dd>
                ) : (
                  <dd className="mt-1">{s.value}</dd>
                )}
              </div>
            ))}
          </dl>
        </>
      )}
      {isAdmin && hasRaw && (
        <div className="mt-6 border-t border-black/10 pt-4">
          <button
            onClick={() => setShowRaw((v) => !v)}
            data-testid="order-summary-toggle-raw"
            className="text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500 hover:text-black inline-flex items-center gap-1"
          >
            {showRaw ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Données techniques (debug)
          </button>
          {showRaw && (
            <pre className="mt-3 text-[11px] font-mono bg-black text-[#FAF8F5] p-3 overflow-x-auto whitespace-pre-wrap" data-testid="order-summary-raw">
{JSON.stringify({ answers: order.answers, technical: order.technical, options: order.options }, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [msgFiles, setMsgFiles] = useState([]);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [workMinutes, setWorkMinutes] = useState("");
  const [priceEdit, setPriceEdit] = useState("");
  const [finalFiles, setFinalFiles] = useState([]);
  const [supReason, setSupReason] = useState("");
  const [supDesc, setSupDesc] = useState("");
  const [supAmount, setSupAmount] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/orders/${id}`);
    setOrder(data);
    setNewStatus(data.status);
    setPriceEdit(String(data.price || 0));
    const m = await api.get(`/orders/${id}/messages`);
    setMessages(m.data);
    // Reset the caller's unread badge server-side
    try { await api.post(`/orders/${id}/mark_read`); } catch {}
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const sendMessage = async () => {
    if (!text.trim() && msgFiles.length === 0) return;
    try {
      await api.post(`/orders/${id}/messages`, { text, file_ids: msgFiles.map((f) => f.id) });
      setText(""); setMsgFiles([]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateStatus = async () => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status: newStatus, note: statusNote });
      setStatusNote("");
      toast.success("Statut mis à jour");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateCreativePhase = async (newPhase) => {
    try {
      await api.patch(`/admin/orders/${id}/creative-phase`, { creative_phase: newPhase });
      toast.success(`Phase mise à jour : ${CREATIVE_PHASE_LABELS[newPhase] || newPhase}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateAdminFields = async () => {
    try {
      const body = {};
      if (priceEdit !== "") body.price = Number(priceEdit);
      if (workMinutes !== "") body.work_minutes = Number(workMinutes);
      if (finalFiles.length) body.final_file_ids = [...(order.final_file_ids || []), ...finalFiles.map((f) => f.id)];
      await api.patch(`/admin/orders/${id}`, body);
      setFinalFiles([]); setWorkMinutes("");
      toast.success("Commande mise à jour");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const proposeSupplement = async () => {
    if (!supReason.trim() || !supAmount) return toast.error("Motif et montant requis");
    try {
      await api.post(`/admin/orders/${id}/supplement`, { reason: supReason, description: supDesc, amount: Number(supAmount) });
      setSupReason(""); setSupDesc(""); setSupAmount("");
      toast.success("Supplément proposé au client");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const decideSupplement = async (sid, decision) => {
    try {
      await api.post(`/orders/${id}/supplement/${sid}/${decision}`);
      toast.success(decision === "accept" ? "Supplément accepté" : "Supplément refusé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!order) return <div className="min-h-[60vh] flex items-center justify-center font-mono text-sm">Chargement…</div>;
  const isAdmin = user?.role === "admin";

  return (
    <div className="cs-cream py-14 min-h-[80vh]" data-testid="page-order-detail">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="cs-eyebrow text-neutral-500">Commande · {order.reference}</div>
            <h1 className="cs-h1 mt-3 text-4xl lg:text-5xl">{order.service_name}</h1>
            <div className="mt-3 text-sm font-mono">
              <span className="cs-status-dot" />{STATUS_LABELS[order.status]}
            </div>
          </div>
          <Link to="/espace" className="cs-btn-ghost !text-black !border-black">← Retour</Link>
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* --- Espace projet créatif (100% additif) --- */}
            <CreativePhaseTimeline currentPhase={order.creative_phase || "echanges_preparation"} />
            <RevisionLinkCard
              orderId={order.id}
              revisionLink={order.revision_link}
              revisionLinkUpdatedAt={order.revision_link_updated_at}
              isAdmin={isAdmin}
              onUpdated={load}
            />
            <ProposalsSection orderId={order.id} isAdmin={isAdmin} />
            <FilesLibrary orderId={order.id} isAdmin={isAdmin} currentUserId={user?.id} refreshKey={messages.length} />

            {/* --- Blocs existants inchangés --- */}
            <CouvertureBriefBlock order={order} />
            <OrderSummaryCard order={order} isAdmin={isAdmin} />
            <QuoteBlock order={order} isAdmin={isAdmin} onReload={load} />
            <OrderBillingSetup key={`${order.id}-${order.billing_party}-${order.payer}-${order.end_client?.id || ""}`} order={order} isAdmin={isAdmin} onReload={load} />
            <PaymentBlock order={order} isAdmin={isAdmin} onReload={load} />
            {isAdmin && <AdminEndClientPayments order={order} onReload={load} />}
            {isAdmin && <BalanceRemindersCard order={order} onReload={load} />}

            <div className="cs-card p-6">
              <div className="cs-eyebrow text-neutral-500">Fichiers reçus</div>
              <ul className="mt-3 text-sm space-y-2">
                {(order.file_ids || []).map((fid) => (
                  <li key={fid} className="flex justify-between items-center">
                    <span className="font-mono text-xs truncate">{fid}</span>
                    <button type="button" onClick={() => openFile(fid)} className="cs-link-underline flex items-center gap-1"><Download size={14} /> Télécharger</button>
                  </li>
                ))}
                {(order.file_ids || []).length === 0 && <li className="text-neutral-500">Aucun fichier.</li>}
              </ul>
              {order.final_file_ids?.length > 0 && (
                <>
                  <div className="cs-eyebrow text-neutral-500 mt-6">Fichiers finaux</div>
                  <ul className="mt-3 text-sm space-y-2">
                    {order.final_file_ids.map((fid) => (
                      <li key={fid} className="flex justify-between items-center">
                        <span className="font-mono text-xs truncate">{fid}</span>
                        <button type="button" onClick={() => openFile(fid)} className="cs-link-underline flex items-center gap-1"><Download size={14} /> Récupérer</button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div className="cs-card p-6">
              <div className="cs-eyebrow text-[#0E7C90]">Échanges & idées</div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-700">
                Cet espace est fait pour construire votre projet ensemble. Vous
                pouvez partager librement vos idées, références, fichiers et
                questions tout au long de la création.
              </p>
              <p className="mt-2 text-[12px] leading-relaxed text-neutral-500 italic">
                Les échanges et discussions sont libres. Les séries d’ajustements
                incluses dans votre prestation concernent uniquement les
                modifications demandées après présentation d’une création.
              </p>
              <ul className="mt-5 space-y-4 max-h-[420px] overflow-y-auto">
                {messages.map((m) => (
                  <li key={m.id} className={`p-4 border ${m.author_role === "admin" ? "border-[#0E7C90]/40 bg-[#0E7C90]/5" : "border-black/10 bg-white"}`}>
                    <div className="flex justify-between text-xs font-mono text-neutral-500"><span>{m.author_name} · {m.author_role}</span><span>{fmtDateTime(m.created_at)}</span></div>
                    <p className="mt-2 whitespace-pre-wrap text-sm">{m.text}</p>
                    {m.file_ids?.length > 0 && (
                      <ul className="mt-2 text-xs">
                        {m.file_ids.map((fid) => (
                          <li key={fid}><button type="button" className="cs-link-underline" onClick={() => openFile(fid)}>Pièce jointe {fid.slice(-6)}</button></li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
                {messages.length === 0 && <li className="text-neutral-500 text-sm">Aucun message pour l'instant.</li>}
              </ul>
              <div className="mt-4 border-t border-black/10 pt-4">
                <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} className="cs-input" placeholder="Écrire un message…" data-testid="message-text" />
                <div className="mt-3"><FileUploader files={msgFiles} setFiles={setMsgFiles} testId="message-files" category="message" /></div>
                <button onClick={sendMessage} className="cs-btn-primary mt-4" data-testid="send-message"><Send size={16} /> Envoyer</button>
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="cs-card p-6">
              <div className="cs-eyebrow text-neutral-500">Récapitulatif</div>
              <div className="mt-3 space-y-2 text-sm">
                <Row k="Prix" v={eur(order.price)} />
                <Row k="Délai" v={order.delay || "—"} />
                <Row k="Options" v={(order.options || []).join(", ") || "—"} />
                <Row k="Créée le" v={fmtDateTime(order.created_at)} />
              </div>
              <button
                type="button"
                onClick={() => openQuotePdf(order.id)}
                className="cs-btn-ghost !text-black !border-black w-full justify-center mt-5 inline-flex items-center gap-2"
                data-testid="download-quote-pdf"
              >
                <FileDown size={16} /> Télécharger le devis PDF
              </button>
              <p className="mt-2 text-[11px] font-mono text-neutral-500 text-center">Référence, résumé, prix et options inclus.</p>
            </div>

            <div className="cs-card p-6">
              <div className="cs-eyebrow text-neutral-500">Historique de statut</div>
              <ol className="mt-3 space-y-3 text-sm">
                {(order.history || []).map((h, i) => (
                  <li key={i} className="border-l-2 border-[#0E7C90] pl-3">
                    <div className="font-medium">{STATUS_LABELS[h.status] || h.status}</div>
                    {h.note && <div className="text-neutral-600 text-xs">{h.note}</div>}
                    <div className="text-neutral-500 font-mono text-[10px] mt-1">{fmtDateTime(h.at)}</div>
                  </li>
                ))}
              </ol>
            </div>

            {isAdmin && (
              <div className="cs-card p-6" data-testid="admin-panel">
                <div className="cs-eyebrow text-[#0E7C90]">Panneau admin</div>
                <div className="mt-4 space-y-4 text-sm">
                  <div>
                    <label className="cs-eyebrow text-neutral-500">Changer le statut</label>
                    <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="cs-input mt-2" data-testid="admin-status-select">
                      {STATUS_LIST.map((s) => {
                        const conflict = s !== order.status && order.status_conflicts?.[s];
                        return <option key={s} value={s} disabled={!!conflict}>{STATUS_LABELS[s]}{conflict ? ` — ${conflict}` : ""}</option>;
                      })}
                    </select>
                    <input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} className="cs-input mt-2" placeholder="Note (optionnel)" />
                    <button onClick={updateStatus} className="cs-btn-primary w-full justify-center mt-3" data-testid="admin-status-save">Enregistrer statut</button>
                  </div>

                  <div className="border-t border-black/10 pt-4">
                    <label className="cs-eyebrow text-[#0E7C90]">Phase du projet</label>
                    <p className="mt-1 text-[12px] text-neutral-600">Progression créative visible côté client. Indépendante du statut de commande.</p>
                    <select
                      value={order.creative_phase || "echanges_preparation"}
                      onChange={(e) => updateCreativePhase(e.target.value)}
                      className="cs-input mt-2"
                      data-testid="admin-creative-phase-select"
                    >
                      {CREATIVE_PHASES.map((p) => (
                        <option key={p.key} value={p.key}>{p.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="border-t border-black/10 pt-4">
                    <div className="cs-eyebrow text-[#0E7C90]">Proposer un supplément</div>
                    <p className="mt-1 text-[12px] text-neutral-600">Aucun supplément ne sera appliqué sans accord du client.</p>
                    <input value={supReason} onChange={(e) => setSupReason(e.target.value)} className="cs-input mt-2" placeholder="Motif (ex : recomposition nécessaire)" data-testid="sup-reason" />
                    <textarea rows={3} value={supDesc} onChange={(e) => setSupDesc(e.target.value)} className="cs-input mt-2" placeholder="Description détaillée" data-testid="sup-desc" />
                    <input type="number" min="0" step="1" value={supAmount} onChange={(e) => setSupAmount(e.target.value)} className="cs-input mt-2" placeholder="Montant (€)" data-testid="sup-amount" />
                    <button onClick={proposeSupplement} className="cs-btn-ghost !text-black !border-black w-full justify-center mt-3" data-testid="sup-submit">Envoyer la proposition</button>
                  </div>

                  <div className="border-t border-black/10 pt-4">
                    <label className="cs-eyebrow text-neutral-500">Prix (€)</label>
                    <input type="number" value={priceEdit} onChange={(e) => setPriceEdit(e.target.value)} className="cs-input mt-2" />
                    <label className="cs-eyebrow text-neutral-500 mt-3 block">Temps de travail (minutes)</label>
                    <input type="number" value={workMinutes} onChange={(e) => setWorkMinutes(e.target.value)} className="cs-input mt-2" placeholder="Ex : 45" data-testid="admin-work-minutes" />
                    {order.hourly_rate !== null && order.hourly_rate !== undefined && (
                      <div className="mt-2 text-[13px] font-mono">CA horaire : <span className={order.hourly_rate >= 65 ? "text-[#0E7C90]" : "text-neutral-700"}>{order.hourly_rate} €/h</span> {order.hourly_rate >= 65 && "· objectif atteint"}</div>
                    )}
                    <label className="cs-eyebrow text-neutral-500 mt-3 block">Fichier(s) final(aux) à téléverser</label>
                    <div className="mt-2"><FileUploader files={finalFiles} setFiles={setFinalFiles} testId="admin-final-files" category="final" /></div>
                    <button onClick={updateAdminFields} className="cs-btn-ghost !text-black !border-black w-full justify-center mt-4" data-testid="admin-fields-save">Enregistrer</button>
                  </div>
                </div>
              </div>
            )}

            {/* Supplements list — visible to both */}
            {(order.supplements || []).length > 0 && (
              <div className="cs-card p-6" data-testid="supplements-list">
                <div className="cs-eyebrow text-[#0E7C90]">Suppléments</div>
                <ul className="mt-4 space-y-3">
                  {order.supplements.map((s) => (
                    <li key={s.id} className={`border p-4 ${s.status === "pending" ? "border-[#0E7C90] bg-[#0E7C90]/5" : s.status === "accepted" ? "border-black/15 bg-white" : "border-black/15 bg-white/50"}`} data-testid={`sup-${s.id}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-serif text-lg">{s.reason}</span>
                        <span className="font-mono">+ {s.amount} €</span>
                      </div>
                      {s.description && <p className="mt-1 text-sm text-neutral-700">{s.description}</p>}
                      <div className="mt-2 text-[12px] font-mono uppercase tracking-[0.14em] text-neutral-500">
                        {s.status === "pending" ? "En attente d'accord" : s.status === "accepted" ? "Accepté" : "Refusé"}
                      </div>
                      {!isAdmin && s.status === "pending" && (
                        <div className="mt-3 flex gap-2">
                          <button onClick={() => decideSupplement(s.id, "accept")} className="cs-btn-primary" data-testid={`sup-accept-${s.id}`}>Accepter</button>
                          <button onClick={() => decideSupplement(s.id, "refuse")} className="cs-btn-ghost !text-black !border-black" data-testid={`sup-refuse-${s.id}`}>Refuser</button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between border-b border-black/10 py-1.5"><span className="text-neutral-500">{k}</span><span className="font-mono text-right">{v}</span></div>;
}
